/**
 * @fileoverview BI数据处理 Hook
 * 
 * 该文件包含了用于处理商业智能(BI)数据的自定义React Hook，主要功能包括：
 * - 处理普通筛选器和级联筛选器
 * - 根据筛选器配置动态生成可用的筛选项
 * - 根据级联筛选器的选择计算相关指标
 * - 支持任意层级的级联筛选器结构
 * 
 * @author AI Assistant
 * @version 1.0.0
 */

import { useMemo } from 'react';
import { DATE_FORMAT_MAP, DATE_OR_TIME_DATA_TYPE, METRIC_DISPLAY_NAME } from '../../../config/constant';
import dateTimeFormat from '@home/utils/dbDataFormat/dateTimeFormat';
import { extractMetricDataKey, sortCurOptionsByAllOptions } from './_utils';

/**
 * 应用筛选器默认值
 * 根据filter_default_value配置和可用选项来设置合理的默认值
 */
const applyFilterDefaultValues = (
  filterDefaultValue: { [key: string]: string; } | undefined,
  dimensionInfo: IDimensionInfo[],
  metricsInfo: IMetricInfo[]
): { filterDefaults: { [key: string]: string; }; defaultMetric: string; } => {
  const filterDefaults: { [key: string]: string; } = {};
  let defaultMetric = '';

  if (!filterDefaultValue) {
    return { filterDefaults, defaultMetric };
  }

  Object.entries(filterDefaultValue).forEach(([key, value]) => {
    if (key === 'metric') {
      // 处理metric特殊字段
      const metricExists = metricsInfo.some(m => m.data_key === value || m.metric_key === value);
      if (metricExists) {
        defaultMetric = value;
      }
    } else {
      // 处理普通筛选器默认值
      const dimension = dimensionInfo.find(d => d.data_key === key);
      if (dimension) {
        const availableValues = dimension.value.map(item => 
          typeof item === 'string' ? item : item.value
        );
        // 检查默认值是否在可选范围内
        if (availableValues.includes(value)) {
          filterDefaults[key] = value;
        }
        // 如果默认值无效，不设置（保留原有逻辑）
      }
    }
  });

  return { filterDefaults, defaultMetric };
};

/**
 * 指标信息接口
 * 用于定义BI系统中的各种指标
 */
export interface IMetricInfo {
  /** 指标显示名称 */
  name: string;
  /** 数据键（用于数值型指标） */
  data_key?: string | {
    data_key: string;
    yAxis_index?: number;
  };
  /** 指标键（用于计算型指标，如百分比） */
  metric_key?: string;
  /** 指标类型：数值型、百分比或浮点数 */
  type: 'numerical' | 'percent' | 'float';
}

/**
 * 维度信息值项接口
 * 用于定义复杂的维度选项，包含图标等UI元素
 */
export interface IDimensionInfoValueItem {
  /** 选项显示名称 */
  name: string;
  /** 选项实际值 */
  value: string;
  /** 普通状态图标 */
  icon: string;
  /** 激活状态图标 */
  icon_active: string;
}

/**
 * 维度信息接口
 * 定义BI系统中的维度数据结构
 */
export interface IDimensionInfo {
  /** 维度显示名称 */
  name: string;
  /** 维度数据键，用于标识该维度 */
  data_key: string;
  /** 维度可选值列表，支持字符串或复杂对象 */
  value: Array<IDimensionInfoValueItem | string>;
}

/**
 * 递归类型定义：支持动态层级的级联筛选器显示值结构
 * 
 * 该类型支持任意深度的嵌套结构：
 * - 中间层级：对象类型，键为选项值，值为下一层级
 * - 最终层级：字符串数组，包含指标的data_key或metric_key
 * 
 * @example
 * // 2层级联
 * {
 *   mobile: {
 *     ios: ['dau_daily', 'revenue']
 *   }
 * }
 * 
 * @example
 * // 3层级联
 * {
 *   games: {
 *     mobile: {
 *       rpg: ['dau_daily', 'revenue']
 *     }
 *   }
 * }
 */
export type ICascadeDisplayValue = {
  [key: string]: ICascadeDisplayValue | string[];
};

/**
 * 筛选器信息接口
 * 定义BI系统中的筛选器配置
 */
export interface IFilterInfo {
  /** 筛选器显示名称 */
  name: string;
  /** 筛选器数据键，用于标识该筛选器 */
  data_key: string;
  /** 筛选器类型：普通筛选器或级联筛选器 */
  filter_type?: 'normal' | 'cascade';
  /** 是否关联指标（仅级联筛选器使用，一个系统中只能有一个关联指标的级联筛选器） */
  has_metric?: boolean;
  /** 级联筛选器的可选值（仅级联筛选器使用） */
  value?: string[];
  /** 级联筛选器的显示层级信息，数组长度决定级联层级数 */
  display_info?: string[];
  /** 级联筛选器的显示值结构，支持动态层级 */
  display_value?: ICascadeDisplayValue;
  /** 仅对normal类型筛选器有效，控制筛选器实际显示的值 */
  visible_value?: string[];
}

/**
 * 显示筛选器项接口
 * Hook返回的筛选器数据结构
 */
export interface IShowFilterItem {
  /** 筛选器显示名称 */
  name: string;
  /** 筛选器数据键 */
  data_key: string;
  /** 筛选器类型 */
  filter_type: 'normal' | 'cascade';
  /** 当前可选的值列表（根据级联关系过滤后的结果） */
  value: Array<IDimensionInfoValueItem | string>;
}

/**
 * Hook返回值接口
 * 定义useBIData Hook的返回数据结构
 */
export interface IUseTabDataReturn {
  /** 处理后的筛选器列表，包含根据级联关系过滤的选项 */
  showFilters: IShowFilterItem[];
  /** 当前应显示的指标列表，根据级联筛选器的选择计算得出 */
  showMetrics: IMetricInfo[];
  /** 内部筛选器值状态，包含默认值和用户选择值 */
  innerFilterInfoVal: { [key: string]: string; };
}

/**
 * Hook参数接口
 * 定义useBIData Hook的输入参数
 */
export interface IUseTabDataParams {
  /** 筛选器配置列表 */
  filterInfo: IFilterInfo[];
  /** 维度信息列表 */
  dimensionInfo: IDimensionInfo[];
  /** 当前筛选器的选择值，可为null */
  filterInfoVal: { [key: string]: string; } | null;
  /** 指标信息列表 */
  metricsInfo: IMetricInfo[];
  /** 筛选器默认值配置 */
  filter_default_value?: { [key: string]: string; };
}

/**
 * BI数据处理 Hook
 * 
 * 该Hook用于处理BI系统的筛选器和指标数据，支持：
 * 1. 普通筛选器：简单的单选筛选
 * 2. 级联筛选器：多层级关联的筛选器，支持任意层级
 * 3. 指标计算：根据级联筛选器的选择动态计算可用指标
 * 4. 默认值处理：为未设置的筛选器提供合理的默认值
 * 
 * @param params - Hook参数对象
 * @param params.filterInfo - 筛选器配置信息
 * @param params.dimensionInfo - 维度数据信息
 * @param params.filterInfoVal - 当前筛选器选择值
 * @param params.metricsInfo - 指标配置信息
 * 
 * @returns 处理后的筛选器、指标和内部状态
 * 
 * @example
 * ```typescript
 * const { showFilters, showMetrics, innerFilterInfoVal } = useBIData({
 *   filterInfo: [
 *     { name: '粒度', data_key: 'granularity', filter_type: 'normal' },
 *     {
 *       name: '游戏类型',
 *       data_key: 'game_type',
 *       filter_type: 'cascade',
 *       has_metric: true,
 *       display_info: ['game_type', 'platform'],
 *       display_value: {
 *         mobile: { ios: ['dau_daily'], android: ['dau_daily'] },
 *         pc: { steam: ['revenue'] }
 *       }
 *     }
 *   ],
 *   dimensionInfo: [
 *     { name: '粒度', data_key: 'granularity', value: ['daily', 'weekly'] },
 *     { name: '游戏类型', data_key: 'game_type', value: ['mobile', 'pc'] },
 *     { name: '平台', data_key: 'platform', value: ['ios', 'android', 'steam'] }
 *   ],
 *   filterInfoVal: { game_type: 'mobile' },
 *   metricsInfo: [
 *     { name: '日活', data_key: 'dau_daily', type: 'numerical' },
 *     { name: '收入', data_key: 'revenue', type: 'numerical' }
 *   ]
 * });
 * ```
 */
export const useBIData = (params: IUseTabDataParams): IUseTabDataReturn => {
  const { filterInfo, dimensionInfo: dimensionInfoVal, filterInfoVal, metricsInfo, filter_default_value } = params;
  const dateFormatter = DATE_FORMAT_MAP?.[filterInfoVal?.granularity as keyof typeof DATE_FORMAT_MAP] || 'day';
  // console.log('useBIData params:', params)
  const { showFilters, showMetrics, innerFilterInfoVal } = useMemo(() => {
    const showFilters: IShowFilterItem[] = [];
    let showMetrics: IMetricInfo[] = [];
    
    // 应用默认值配置
    const { filterDefaults } = applyFilterDefaultValues(filter_default_value, dimensionInfoVal, metricsInfo);
    let innerFilterInfoVal: { [key: string]: string; } = { ...filterDefaults, ...(filterInfoVal || {}) };
    // 格式化日期维度
    const dimensionInfo = dimensionInfoVal?.map(item => {
      if (DATE_OR_TIME_DATA_TYPE?.includes(item?.data_key)) {
        item.value = item?.value?.map((date: any) => dateTimeFormat(date as string, dateFormatter));
      }
      return item;
    });

    filterInfo.forEach((filter) => {
      // 处理普通筛选器
      if (filter.filter_type === 'normal') {
        const findDimensionInfo = dimensionInfo.find(
          (item) => item.data_key === filter.data_key
        );

        if (findDimensionInfo) {
          showFilters.push({
            ...findDimensionInfo,
            filter_type: 'normal',
          });

          if (!innerFilterInfoVal[filter.data_key]) {
            const firstValue = findDimensionInfo?.value?.[0];
            if (firstValue) {
              innerFilterInfoVal[filter.data_key] =
                typeof firstValue === 'string' ? firstValue : firstValue.value || '';
            } else {
              innerFilterInfoVal[filter.data_key] = '';
            }
          }
        }
      }

      // 处理级联筛选器
      if (filter.filter_type === 'cascade') {
        const { display_info: displayInfo = [], display_value: displayValue = {} } = filter;

        // 处理主级联筛选器
        const mainFilter = dimensionInfo.find(item => item.data_key === filter.data_key);
        if (!mainFilter) return;

        showFilters.push({
          ...mainFilter,
          filter_type: 'cascade',
        });

        // 设置主筛选器默认值
        if (!innerFilterInfoVal[filter.data_key]) {
          const firstValue = mainFilter.value[0];
          if (firstValue) {
            innerFilterInfoVal[filter.data_key] =
              typeof firstValue === 'string' ? firstValue : firstValue.value || '';
          }
        }

        // 处理级联子层级
        const currentPathValues: { [key: string]: string; } = { ...innerFilterInfoVal };
        let currentLevel: ICascadeDisplayValue | string[] | undefined = displayValue;
        let cascadePathValid = true;
        const parentLevels: ICascadeDisplayValue[] = []; // 新增：保存各级父层级

        for (let i = 0;i < displayInfo.length;i++) {
          const currentDimKey = displayInfo[i];
          const dimInfo = dimensionInfo.find(item => item.data_key === currentDimKey);
          if (!dimInfo) break;

          if (!cascadePathValid) break;

          // 设置当前层级的默认值
          // if (!currentPathValues[currentDimKey] && currentLevel && !Array.isArray(currentLevel)) {
          //   const dimensionData = dimensionInfoVal?.find(item => item.data_key === currentDimKey);
          //   const allOptions = dimensionData?.value || [];
          //   const curOptions = Object.keys(currentLevel);
          //   const availableOptions = sortCurOptionsByAllOptions(allOptions as Array<string | { value: string }>, curOptions);
          //   // debugger
          //   if (availableOptions.length > 0) {
          //     const selectedValue = filterInfoVal?.[currentDimKey] || availableOptions[0];
          //     if (availableOptions.includes(selectedValue)) {
          //       currentPathValues[currentDimKey] = selectedValue;
          //     } else {
          //       currentPathValues[currentDimKey] = availableOptions[0];
          //     }
          //   }
          // }

          // 设置/校验当前层级的值（无效值回退首个可用项）
          if (currentLevel && !Array.isArray(currentLevel)) {
            const allOptions = dimensionInfoVal?.find(item => item.data_key === currentDimKey)?.value || [];
            const curOptions = Object.keys(currentLevel);
            const availableOptions = sortCurOptionsByAllOptions(allOptions, curOptions);
            if (availableOptions.length > 0) {
              const candidate = filterInfoVal?.[currentDimKey] || currentPathValues[currentDimKey];
              currentPathValues[currentDimKey] = availableOptions.includes(candidate) && candidate
                ? candidate
                : availableOptions[0];
            }
          }

          // 保存当前层级作为后续参考
          if (currentLevel && !Array.isArray(currentLevel)) {
            parentLevels.push(currentLevel); // 保存当前层级
          }

          // 更新当前层级路径
          if (currentLevel && !Array.isArray(currentLevel) && currentPathValues[currentDimKey]) {
            const nextLevel: ICascadeDisplayValue | string[] | undefined = currentLevel[currentPathValues[currentDimKey]];
            if (nextLevel === undefined) {
              cascadePathValid = false;
            } else {
              currentLevel = nextLevel;
            }
          }

          // 添加筛选器到显示列表
          if (i > 0 && cascadePathValid) {
            const parentLevel = parentLevels[i-1]; // 获取父层级
            const parentValue = currentPathValues[displayInfo[i-1]];
            
            let availableValues: string[] = [];
            if (parentLevel && parentValue) {
              const parentLevelValue = parentLevel[parentValue];
              if (parentLevelValue && !Array.isArray(parentLevelValue)) {
                availableValues = Object.keys(parentLevelValue);
              }
            }

            showFilters.push({
              ...dimInfo,
              filter_type: 'cascade',
              value: dimInfo.value.filter(item => {
                const value = typeof item === 'string' ? item : item.value;
                return availableValues.length === 0 || availableValues.includes(value);
              }),
            });

            // 校验子级当前值（无效回退首个可用项）
            if (availableValues.length > 0) {
              const curVal = currentPathValues[currentDimKey];
              currentPathValues[currentDimKey] = availableValues.includes(curVal) && curVal
                ? curVal
                : availableValues[0];
            }
          }

          // 处理指标显示
          if (i === displayInfo.length - 1 && Array.isArray(currentLevel)) {
            showMetrics = currentLevel
              .map((metricKey) => metricsInfo.find((m) => {
                const mKey =
                    extractMetricDataKey(m.data_key) || m.metric_key || '';
                return mKey === metricKey;
              }))
              .filter(Boolean) as IMetricInfo[];
          }
        }

        // 更新内部状态
        innerFilterInfoVal = { ...innerFilterInfoVal, ...currentPathValues };
      }
    });

    // 如果没有指标被选中，显示所有指标
    if (showMetrics.length === 0) {
      showMetrics = metricsInfo?.map((metric) => {
        const key =
          extractMetricDataKey(metric.data_key) || metric.metric_key || '';
        return {
          ...metric,
          name: metric?.name || METRIC_DISPLAY_NAME?.[key] || key || '',
        };
      }) || [];
    }

    return {
      showFilters,
      showMetrics,
      innerFilterInfoVal,
    };
  }, [filterInfo, dimensionInfoVal, filterInfoVal, metricsInfo, filter_default_value]);
    // console.log('showFiltersshowFilters', showFilters)
  return {
    showFilters,
    showMetrics,
    innerFilterInfoVal,
  };
};